import { useMemo } from "react";
import type { ReactNode } from "react";
import { styled } from "@mui/material";
import MobileStepper, { mobileStepperClasses } from "@mui/material/MobileStepper";
import type { MobileStepperProps } from "@mui/material/MobileStepper";
import { mergeSlotProps } from "@mui/material/utils";
import useCarousel from "../hooks/useCarousel";

const CarouselStepperRoot = styled(MobileStepper)(({ theme }) => ({
  justifyContent: "center",
  padding: 0,
  backgroundColor: "transparent",

  [`& .${mobileStepperClasses.dot}`]: {
    backgroundColor: theme.palette.grey[60],
  },
  [`& .${mobileStepperClasses.dotActive}`]: {
    backgroundColor: theme.palette.primary.main,
  },
}));

export interface CarouselStepperProps extends Omit<
  MobileStepperProps,
  "steps" | "activeStep" | "variant" | "backButton" | "nextButton"
> {
  backButton?: ReactNode;
  nextButton?: ReactNode;
}

/**
 * @description Dots marking the visible slide. They are hidden from assistive technologies,
 * which already hear the position from the slide label.
 *
 * @see https://mui.com/material-ui/react-stepper/#dots
 */
function CarouselStepper(props: CarouselStepperProps) {
  const { backButton = null, nextButton = null, slotProps, ...rest } = props;
  const { activeStep, steps } = useCarousel();

  const memoSlotProps = useMemo<MobileStepperProps["slotProps"]>(
    () => ({ ...slotProps, dots: mergeSlotProps({ "aria-hidden": true }, slotProps?.dots ?? {}) }),
    [slotProps]
  );

  return (
    <CarouselStepperRoot
      variant="dots"
      position="static"
      {...rest}
      steps={steps}
      activeStep={activeStep}
      backButton={backButton}
      nextButton={nextButton}
      slotProps={memoSlotProps}
    />
  );
}

export default CarouselStepper;
